import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { Notification } from '../components/Notification';
import type { NotificationAction } from '../components/Notification';

import styles from './NotificationProvider.module.css';
import { notificationStore } from './store';
import type { NotificationEntry } from './store';

/** Kept in sync with the exit keyframe duration in `NotificationProvider.module.css`. */
export const EXIT_ANIMATION_MS = 450;

export interface NotificationHostProps {
  entry: NotificationEntry;
  /** True once the entry has left the store — it plays its exit animation, then calls `onExited`. */
  leaving: boolean;
  /** Called after the exit animation so the provider can drop the item from the DOM. */
  onExited: (id: string) => void;
  /** Auto-dismiss delay applied to action-less toasts that don't set their own `duration`. */
  defaultDuration: number;
}

/**
 * Wraps one queued toast in its `<li>` and owns its auto-dismiss timer. A toast auto-dismisses
 * only when it has no actions (or an explicit `duration`); the countdown pauses while the pointer
 * is over it or focus is inside, so it can't vanish mid-interaction (WCAG 2.2.1). While `leaving`
 * it plays its exit animation and then asks the provider to unmount it.
 */
export function NotificationHost({
  entry,
  leaving,
  onExited,
  defaultDuration,
}: NotificationHostProps) {
  const { id, options } = entry;
  const { duration: durationOption, ...surface } = options;
  const hasActions = (surface.actions?.length ?? 0) > 0;
  const duration = durationOption ?? (hasActions ? 0 : defaultDuration);
  const [paused, setPaused] = useState(false);

  // Auto-dismiss countdown (suspended while paused or already leaving).
  useEffect(() => {
    if (leaving || duration <= 0 || paused) return undefined;
    const timer = setTimeout(() => {
      notificationStore.dismiss(id, { reason: 'timeout' });
    }, duration);
    return () => {
      clearTimeout(timer);
    };
  }, [id, duration, paused, leaving]);

  // Unmount once the exit animation has had time to play (timer-driven, so it survives
  // prefers-reduced-motion where no animation — hence no animationend — fires).
  useEffect(() => {
    if (!leaving) return undefined;
    const timer = setTimeout(() => {
      onExited(id);
    }, EXIT_ANIMATION_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [leaving, id, onExited]);

  const actions: NotificationAction[] = (surface.actions ?? []).map((action) => ({
    ...action,
    onClick: () => {
      action.onClick?.();
      if (action.dismiss !== false) {
        notificationStore.dismiss(id, { action: action.value ?? action.label, reason: 'action' });
      }
    },
  }));

  return (
    // The list item only hosts hover/focus handlers to pause its own auto-dismiss timer; the
    // interactive controls live in the Notification itself.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <li
      className={classNames(styles.item, leaving && styles.itemLeaving)}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      onFocusCapture={() => {
        setPaused(true);
      }}
      onBlurCapture={() => {
        setPaused(false);
      }}
    >
      <Notification
        {...surface}
        actions={actions}
        onClose={() => {
          notificationStore.dismiss(id, { reason: 'close' });
        }}
      />
    </li>
  );
}
